import React from "react";
import { Link } from "react-router-dom";
import PROJECT_CONFIG from "../config/projectConfig";

/** Aerial coastal waters - maps cleanly to flood hazard / NFIP context */
const HERO_IMAGE = "/images/hero-coastal.jpg";
const EXPERTISE_IMAGE = "/images/ocean-waves.jpg";

const PROCESS_STEPS = [
  {
    step: "01",
    title: "Community consultation first",
    text: "FEMA works with communities to resolve flood map challenges before a panel is formed.",
  },
  {
    step: "02",
    title: "Panel convened",
    text: "When consultation cannot resolve the conflict, NIBS establishes an independent SRP panel.",
  },
  {
    step: "03",
    title: "Scientific review",
    text: "Experts examine FEMA data and community-submitted technical evidence for accuracy.",
  },
  {
    step: "04",
    title: "Written recommendation",
    text: "The panel issues a decision that accepts or denies alternative flood hazard data for FEMA.",
  },
];

const EXPERTISE = [
  "Coastal storm & hydraulic engineering",
  "Geotechnical engineering",
  "Hydrology & alluvial fan analysis",
  "Coastal geology & geomorphology",
  "Floodplain management",
  "GIS / mapping science",
];

const Landing = () => {
  const name = PROJECT_CONFIG.name;
  const fullName = PROJECT_CONFIG.fullName;

  return (
    <div className="bg-white text-srp-ink">
      {/* Hero */}
      <section
        className="relative isolate min-h-[calc(100svh-4.5rem)] overflow-hidden text-white"
        aria-labelledby="landing-hero-heading"
      >
        <div className="absolute inset-0 -z-10">
          <img
            src={HERO_IMAGE}
            alt=""
            className="h-full w-full object-cover object-center motion-safe:animate-hero-zoom"
            decoding="async"
            fetchPriority="high"
          />
          <div
            className="absolute inset-0 bg-gradient-to-r from-srp-ink/95 via-custom-blue/85 to-custom-blue/45"
            aria-hidden="true"
          />
          <div
            className="absolute inset-0 bg-gradient-to-t from-srp-ink/80 via-transparent to-srp-ink/30"
            aria-hidden="true"
          />
        </div>

        <div className="relative mx-auto flex min-h-[calc(100svh-4.5rem)] max-w-7xl flex-col justify-end px-6 pb-16 pt-24 sm:px-8 sm:pb-20 lg:justify-center lg:px-10 lg:pb-24 lg:pt-16">
          <div className="max-w-3xl">
            <p className="text-5xl font-bold tracking-tight text-white sm:text-6xl md:text-7xl motion-safe:animate-hero-rise">
              {name}
            </p>
            <p className="mt-2 text-sm font-semibold uppercase tracking-[0.22em] text-custom-cyan-bright sm:text-base motion-safe:animate-hero-rise">
              {fullName}
            </p>

            <h1
              id="landing-hero-heading"
              className="mt-8 text-3xl font-bold leading-tight text-white sm:text-4xl md:text-5xl motion-safe:animate-hero-rise-delay"
            >
              Independent science for flood map resolution.
            </h1>

            <p className="mt-5 max-w-xl text-lg leading-relaxed text-white sm:text-xl motion-safe:animate-hero-rise-delay">
              When FEMA and communities disagree on flood hazard data, {name}{" "}
              convenes expert panels to review the science and recommend a clear
              path forward.
            </p>

            <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center motion-safe:animate-hero-rise-delay-2">
              <Link
                to="/cadre-application"
                className="inline-flex items-center justify-center bg-white px-7 py-3.5 text-base font-semibold text-custom-blue transition hover:bg-srp-foam focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                Apply to the Cadre
              </Link>
              <Link
                to="/process"
                className="inline-flex items-center justify-center border-2 border-white bg-transparent px-7 py-3.5 text-base font-semibold text-white transition hover:bg-white/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                How the process works
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Purpose */}
      <section
        className="border-b border-custom-blue/10 bg-srp-foam"
        aria-labelledby="landing-purpose-heading"
      >
        <div className="mx-auto max-w-7xl px-6 py-20 sm:px-8 lg:px-10">
          <div className="max-w-3xl">
            <h2
              id="landing-purpose-heading"
              className="text-3xl font-bold text-custom-blue sm:text-4xl"
            >
              Neutral expertise. Faster resolution.
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-srp-ink">
              Under contract with FEMA, the National Institute of Building
              Sciences establishes {name} panels to independently review
              scientific and technical flood hazard data for the National Flood
              Insurance Program, so communities and FEMA can move from conflict
              to a documented recommendation.
            </p>
          </div>

          <ul className="mt-12 grid gap-8 border-t border-custom-blue/15 pt-12 sm:grid-cols-3">
            <li>
              <p className="text-sm font-semibold uppercase tracking-wide text-custom-light-blue">
                Neutral
              </p>
              <p className="mt-2 text-base leading-relaxed text-srp-ink">
                A process designed to be fair and independent for FEMA and
                communities alike.
              </p>
            </li>
            <li>
              <p className="text-sm font-semibold uppercase tracking-wide text-custom-light-blue">
                Expert-led
              </p>
              <p className="mt-2 text-base leading-relaxed text-srp-ink">
                Panels draw on deep coastal, hydrologic, and mapping expertise
                to evaluate conflicting data.
              </p>
            </li>
            <li>
              <p className="text-sm font-semibold uppercase tracking-wide text-custom-light-blue">
                Decisive
              </p>
              <p className="mt-2 text-base leading-relaxed text-srp-ink">
                Written recommendations help resolve flood map challenges when
                consultation alone is not enough.
              </p>
            </li>
          </ul>
        </div>
      </section>

      {/* Process */}
      <section
        className="bg-white"
        aria-labelledby="landing-process-heading"
      >
        <div className="mx-auto max-w-7xl px-6 py-20 sm:px-8 lg:px-10">
          <div className="max-w-2xl">
            <h2
              id="landing-process-heading"
              className="text-3xl font-bold text-custom-blue sm:text-4xl"
            >
              How {name} works
            </h2>
            <p className="mt-4 text-lg text-srp-ink">
              A clear path from unresolved flood map conflict to an independent
              scientific recommendation.
            </p>
          </div>

          <ol className="mt-14 space-y-0 border-t border-custom-blue/15">
            {PROCESS_STEPS.map((item) => (
              <li
                key={item.step}
                className="grid gap-4 border-b border-custom-blue/10 py-8 sm:grid-cols-[5rem_1fr] sm:gap-8"
              >
                <span className="text-3xl font-bold text-custom-light-blue tabular-nums">
                  {item.step}
                </span>
                <div>
                  <h3 className="text-xl font-bold text-custom-blue">
                    {item.title}
                  </h3>
                  <p className="mt-2 max-w-2xl leading-relaxed text-srp-ink">
                    {item.text}
                  </p>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-10">
            <Link
              to="/process"
              className="inline-flex font-semibold text-custom-light-blue underline underline-offset-4 hover:text-custom-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custom-blue"
            >
              Read the full process
            </Link>
          </div>
        </div>
      </section>

      {/* Expertise band with secondary visual */}
      <section
        className="relative overflow-hidden bg-custom-blue text-white"
        aria-labelledby="landing-expertise-heading"
      >
        <div
          className="pointer-events-none absolute inset-y-0 right-0 hidden w-1/2 bg-cover bg-center opacity-30 lg:block"
          style={{
            backgroundImage: `url(${EXPERTISE_IMAGE})`,
          }}
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute inset-y-0 right-0 hidden w-1/2 bg-gradient-to-r from-custom-blue to-transparent lg:block"
          aria-hidden="true"
        />

        <div className="relative mx-auto max-w-7xl px-6 py-20 sm:px-8 lg:px-10">
          <div className="max-w-xl">
            <h2
              id="landing-expertise-heading"
              className="text-3xl font-bold sm:text-4xl"
            >
              Who serves on a panel
            </h2>
            <p className="mt-4 text-lg text-white/90">
              Cadre members typically bring 10+ years of proven experience in
              fields that shape flood hazard science and mapping.
            </p>
            <ul className="mt-8 space-y-3">
              {EXPERTISE.map((item) => (
                <li key={item} className="flex items-start gap-3 text-base">
                  <span
                    className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-custom-cyan-bright"
                    aria-hidden="true"
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/about"
              className="mt-10 inline-flex font-semibold text-custom-cyan-bright underline underline-offset-4 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              Learn more about {name}
            </Link>
          </div>
        </div>
      </section>

      {/* Roles */}
      <section className="bg-white" aria-labelledby="landing-path-heading">
        <div className="mx-auto max-w-7xl px-6 py-20 sm:px-8 lg:px-10">
          <h2
            id="landing-path-heading"
            className="text-3xl font-bold text-custom-blue sm:text-4xl"
          >
            Built for every role in the process
          </h2>
          <p className="mt-4 max-w-2xl text-lg text-srp-ink">
            One platform for experts, communities, and administrators working
            toward scientifically grounded flood map decisions.
          </p>

          <ul className="mt-14 grid gap-12 border-t border-custom-blue/15 pt-12 md:grid-cols-3 md:gap-10">
            <li>
              <h3 className="text-2xl font-bold text-custom-blue">Experts</h3>
              <p className="mt-3 leading-relaxed text-srp-ink">
                Join the cadre if you bring deep coastal, hydrologic, or related
                expertise, and serve on panels when your skills match the case.
              </p>
              <Link
                to="/cadre-application"
                className="mt-4 inline-block font-semibold text-custom-light-blue underline underline-offset-4 hover:text-custom-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custom-blue"
              >
                Start your application
              </Link>
            </li>
            <li>
              <h3 className="text-2xl font-bold text-custom-blue">
                Communities
              </h3>
              <p className="mt-3 leading-relaxed text-srp-ink">
                Follow active panels, understand the rules, and see how
                conflicting flood hazard data moves toward resolution.
              </p>
              <Link
                to="/panels"
                className="mt-4 inline-block font-semibold text-custom-light-blue underline underline-offset-4 hover:text-custom-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custom-blue"
              >
                View panels
              </Link>
            </li>
            <li>
              <h3 className="text-2xl font-bold text-custom-blue">
                Administrators
              </h3>
              <p className="mt-3 leading-relaxed text-srp-ink">
                Form panels, manage shortlists, and keep the scientific review
                process organized from intake to recommendation.
              </p>
              <Link
                to="/login"
                className="mt-4 inline-block font-semibold text-custom-light-blue underline underline-offset-4 hover:text-custom-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custom-blue"
              >
                Sign in
              </Link>
            </li>
          </ul>
        </div>
      </section>

      {/* Resources */}
      <section
        className="border-t border-custom-blue/10 bg-srp-foam"
        aria-labelledby="landing-resources-heading"
      >
        <div className="mx-auto max-w-7xl px-6 py-20 sm:px-8 lg:px-10">
          <h2
            id="landing-resources-heading"
            className="text-3xl font-bold text-custom-blue sm:text-4xl"
          >
            Explore the program
          </h2>
          <p className="mt-4 max-w-2xl text-lg text-srp-ink">
            Guidance, rules, and answers for anyone participating in or
            following the {name} process.
          </p>

          <ul className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                to: "/rules-procedures",
                title: "Rules & procedures",
                text: "How panels operate and decide.",
              },
              {
                to: "/faqs",
                title: "FAQs",
                text: "Common questions about {name}.".replace("{name}", name),
              },
              {
                to: "/resources",
                title: "Resources",
                text: "Documents and reference materials.",
              },
              {
                to: "/contact",
                title: "Contact",
                text: "Reach the {name} program team.".replace("{name}", name),
              },
            ].map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className="group block h-full border-t-2 border-custom-blue pt-5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custom-blue"
                >
                  <h3 className="text-lg font-bold text-custom-blue group-hover:text-custom-light-blue">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-srp-ink">
                    {item.text}
                  </p>
                  <span className="mt-4 inline-block text-sm font-semibold text-custom-light-blue">
                    Open →
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Closing CTA */}
      <section
        className="bg-custom-blue text-white"
        aria-labelledby="landing-cta-heading"
      >
        <div className="mx-auto flex max-w-7xl flex-col gap-8 px-6 py-20 sm:px-8 lg:flex-row lg:items-end lg:justify-between lg:px-10">
          <div className="max-w-2xl">
            <h2
              id="landing-cta-heading"
              className="text-3xl font-bold sm:text-4xl"
            >
              Ready to contribute your expertise?
            </h2>
            <p className="mt-4 text-lg text-white">
              Cadre members help deliver the independent scientific review FEMA
              and communities rely on.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link
              to="/cadre-application"
              className="inline-flex shrink-0 items-center justify-center bg-white px-7 py-3.5 text-base font-semibold text-custom-blue transition hover:bg-srp-foam focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              Apply to the Cadre
            </Link>
            <Link
              to="/contact"
              className="inline-flex shrink-0 items-center justify-center border-2 border-white px-7 py-3.5 text-base font-semibold text-white transition hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              Contact the team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Landing;
